import React from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../hooks/redux';
import { dismissBanner } from '../../../services/OnboardingService';
import {
  setPendingNavigation,
  setShowWarningModal
} from '../../../store/slices/accountSlice';
import { TUTORIAL_GUIDE_STEPS } from '../../features/TutorialGuide/TutorialGuide.constants';
import type { GuideStepKey } from '../../../types/OnboardingGuide.types';
import {
  NavigationItem,
  VerticalNavigationProps
} from './VerticalNavigation.types';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATHS } from '../../../constants/Paths.constants';
import { TutorialBanner } from '../../features/TutorialGuide/TutorialBanner';
import { startTutorialGuideFlow } from '../../../utils/Functions.utils';
import { setShowTutorialBanner } from '../../../store/slices/tutorialGuideSlice';
import { selectIsSuperAdmin } from '../../../store/selectors/authSelectors';

export function VerticalNavigation({
  navigationItems,
  logoSrc,
  logoAlt,
  logoClassName = 'h-12 w-36',
  miniLogoSrc,
  onItemClick,
  isMobileMenuOpen,
  onMobileMenuClose
}: VerticalNavigationProps) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { personalInfoChanged, generalSettingsChanged } = useAppSelector(
    (state) => state.account
  );
  const {
    isOpen: isTutorialGuideOpen,
    currentStepKey,
    showTutorialBanner
  } = useAppSelector((state) => state.tutorialGuide);

  const isSuperAdminRole = useAppSelector(selectIsSuperAdmin);

  const visibleNavigationItems = isSuperAdminRole
    ? navigationItems.filter(
        (item) => item.name !== 'Dashboard' && item.name !== 'AI Systems'
      )
    : navigationItems;

  const currentTutorialStep = TUTORIAL_GUIDE_STEPS.find(
    (step) => step.key === currentStepKey
  );
  const tutorialStepToHref: Partial<Record<GuideStepKey, string>> = {
    DASHBOARD_OVERVIEW: PATHS.DASHBOARD,
    AI_SYSTEMS_OVERVIEW: PATHS.AI_SYSTEMS,
    USER_MANAGEMENT_OVERVIEW: PATHS.USER_MANAGEMENT,
    SETTINGS_OVERVIEW: PATHS.SETTINGS
  };
  const emphasizedTutorialHref =
    isTutorialGuideOpen && currentTutorialStep
      ? tutorialStepToHref[currentTutorialStep.key]
      : undefined;

  const getNavigationItemClassName = (item: NavigationItem) => {
    const baseClasses =
      'flex lg:flex-col 2xl:flex-row lg:min-h-[60px] 2xl:min-h-10 items-center px-2 py-2 rounded-md text-sm font-medium leading-5 ';
    const animationClasses = 'transition-all duration-200 hover:scale-105 ';

    if (emphasizedTutorialHref) {
      return `${baseClasses}${item.href === emphasizedTutorialHref ? 'cursor-default text-white bg-zinc-50/40 ring-4 ring-white/40 relative z-50' : 'text-white'}`;
    }

    if (isTutorialGuideOpen) {
      return `${baseClasses}${item.current ? 'text-white bg-zinc-50/40' : 'text-gray-200'}`;
    }

    return `${baseClasses}${animationClasses}${item.current ? 'text-white bg-zinc-50/40' : 'text-gray-200 hover:bg-white hover:bg-opacity-10 hover:text-white hover:text-opacity-100'}`;
  };

  const handleItemClick = (
    e: React.MouseEvent,
    item: (typeof visibleNavigationItems)[0]
  ) => {
    e.preventDefault();

    if (isTutorialGuideOpen) return;

    if (location.pathname === PATHS.QUESTIONNAIRE) {
      dispatch(setPendingNavigation(item.href));
      dispatch(setShowWarningModal(true));
      return;
    }

    if (personalInfoChanged || generalSettingsChanged) {
      dispatch(setPendingNavigation(item.href));
      dispatch(setShowWarningModal(true));
    } else {
      if (onItemClick) {
        onItemClick(item);
      }
    }
    // Zatvori mobilni meni nakon klika
    if (onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const onStartTour = async () => {
    try {
      await startTutorialGuideFlow(
        dispatch,
        'BANNER',
        navigate,
        location.pathname
      );
      dispatch(setShowTutorialBanner(false));
    } catch (error) {
      console.error('Error starting onboarding guide:', error);
    }
  };

  const onDismiss = async () => {
    dispatch(setShowTutorialBanner(false));

    try {
      await dismissBanner();
    } catch (error) {
      console.error('Error dismissing onboarding banner:', error);
    }
  };

  return (
    <>
      {/* Uska navigacija - od lg do 2xl */}
      <div
        className="hidden lg:flex 2xl:hidden h-screen flex-col fixed left-0 top-0 z-30 transition-all duration-300"
        style={{
          background: 'linear-gradient(180deg, #1A0735 0%, #2B2166 100%)',
          width: '100px'
        }}
      >
        {isTutorialGuideOpen && (
          <div className="absolute inset-0 z-40 bg-gray-950/66" />
        )}
        <div className="px-2 py-5 flex items-center justify-center">
          <img
            src={miniLogoSrc || logoSrc}
            alt={logoAlt}
            className="h-16 w-17 object-contain"
            style={{ height: '64px', width: '68px' }}
          />
        </div>
        <nav className="flex-1 px-2">
          <ul className="flex flex-col gap-1">
            {visibleNavigationItems.map((item) => (
              <li key={item.name}>
                <a
                  href={item.href}
                  onClick={(e) => handleItemClick(e, item)}
                  className={getNavigationItemClassName(item)}
                >
                  <div className="mb-2 w-5 h-5">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <span className="text-center leading-none font-medium text-xs">
                    {item.name}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
        {showTutorialBanner && (
          <TutorialBanner onStartTour={onStartTour} onDismiss={onDismiss} />
        )}
      </div>
      {/* Desktop Navigation - prikazuje se samo na 2xl+ */}
      <div
        className="hidden z-30 2xl:flex w-[256px] h-screen flex-col fixed left-0 top-0"
        style={{
          background: 'linear-gradient(180deg, #1A0735 0%, #2B2166 100%)'
        }}
      >
        {isTutorialGuideOpen && (
          <div className="absolute w-full h-full z-40 bg-gray-950/66" />
        )}
        <div className="px-6 py-5 flex items-center">
          <img src={logoSrc} alt={logoAlt} className={logoClassName} />
        </div>
        <nav className="flex-1 px-3">
          <ul className="space-y-1">
            {visibleNavigationItems.map((item) => (
              <li key={item.name}>
                <a
                  href={item.href}
                  onClick={(e) => handleItemClick(e, item)}
                  className={getNavigationItemClassName(item)}
                >
                  <item.icon />
                  {item.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        {showTutorialBanner && (
          <TutorialBanner onStartTour={onStartTour} onDismiss={onDismiss} />
        )}
      </div>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex flex-col"
          style={{
            background: 'linear-gradient(180deg, #1A0735 0%, #2B2166 100%)'
          }}
        >
          <div className="p-6 flex items-center justify-between border-b border-white border-opacity-10">
            <img src={logoSrc} alt={logoAlt} className={logoClassName} />
            <button
              onClick={onMobileMenuClose}
              id="vertical-navigation-mobile-close-button"
              data-testid="vertical-navigation-mobile-close-button"
              className="p-2 text-white hover:bg-white hover:bg-opacity-10 rounded-md transition-colors duration-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <nav className="flex-1 px-6 py-4">
            <ul className="space-y-1">
              {visibleNavigationItems.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    onClick={(e) => handleItemClick(e, item)}
                    className={`flex items-center px-4 py-3 w-full rounded-md text-sm font-medium leading-5 font-sans transition-all duration-200 ${
                      item.current
                        ? 'text-white'
                        : 'text-gray-200 hover:bg-white hover:bg-opacity-10 hover:text-white hover:text-opacity-100'
                    }`}
                    style={item.current ? { background: '#F7F7F840' } : {}}
                  >
                    <item.icon />
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </>
  );
}
